import { useTranslator } from "@eo-locale/react";
import { boolean } from "yup";
import { ChangeEvent } from "react";

import type { Question } from "../question";
import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  Checkbox,
  VisuallyHidden
} from "@/storybook-components";

export const validate = async (
  answer: string[],
  question: Question
): Promise<string[]> => {
  let schema = boolean();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(answer);
    return [];
  } catch (err: any) {
    return err.errors;
  }
};

function CheckBoxQuestion(props: Question): JSX.Element {
  const {
    id,
    question,
    required,
    hasChecked,
    error,
    status,
    onChangeHandler,
    notes,
    hasLabel
  } = props;
  const { translate } = useTranslator();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (onChangeHandler) onChangeHandler(event.target.checked);
  };

  return (
    <FormControl
      my={4}
      id={`${id}`}
      isInvalid={error?.length > 0 || false}
      isRequired={required || false}
      isDisabled={status === "saving"}
      className={error?.length > 0 ? "ehqQuestionError" : ""}
      sx={{
        ".chakra-checkbox__control": { marginTop: 1 },
        ".chakra-checkbox__label": {
          marginTop: "auto",
          "> span": { fontWeight: "normal" }
        }
      }}
    >
      {hasLabel && (
        <FormLabel as="legend">
          <HTMLWrapper html={question} />
          {required && (
            <VisuallyHidden>{`* ${translate(
              "common.required"
            )}`}</VisuallyHidden>
          )}
        </FormLabel>
      )}

      <Checkbox
        onChange={handleChange}
        isChecked={hasChecked}
        size={{ base: "lg", xl: "md" }}
        isDisabled={status === "saving"}
        alignItems="baseline"
      >
        <HTMLWrapper html={question} />
        {required && (
          <VisuallyHidden>{`${translate("common.required")}`}</VisuallyHidden>
        )}
      </Checkbox>
      {error?.length > 0 && (
        <FormErrorMessage data-testid="checkboxQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{notes}</FormHelperText>
    </FormControl>
  );
}

export { CheckBoxQuestion };
